import { useEffect, useState } from "react";

import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import OpportunityCounterOfferForm from "@/components/forms/offer/counter";
import PaymentButton from "./payment";

import { useAcceptOffer } from "@/mutations/opportunity/accept-offer";

import dateStringToLocalDate from "@/utils/dateStringToLocalDate";

import {
  Billing,
  CandidateJobOpening,
  CandidateStatus,
  CompanyJobOpening,
  MinimalCandidateStatus,
  NewBillingTerm,
  Offer,
  OfferTerm,
} from "@/types";

const ViewOfferCompany = ({
  candidate,
  opportunity,
}: {
  candidate: CandidateStatus | MinimalCandidateStatus;
  opportunity: CompanyJobOpening | CandidateJobOpening;
}) => {
  const [viewOfferDialogOpen, setViewOfferDialogOpen] = useState(false);
  const [counterViewOpen, setCounterViewOpen] = useState(false);

  const offer = candidate.offers.at(-1);
  const billing = candidate.billing;

  useEffect(() => {
    if (!viewOfferDialogOpen) {
      setCounterViewOpen(false);
    }
  }, [viewOfferDialogOpen]);

  let variant: "secondary" | "outline" = "secondary";
  let content: JSX.Element | undefined = undefined;
  if (billing) {
    content = (
      <BillingEstablishedView
        billing={billing}
        candidate={candidate}
        opportunity={opportunity}
        closeFn={() => setViewOfferDialogOpen(false)}
      />
    );

    if (
      billing.companyCheckoutStatus === "completed" ||
      billing.companyCheckoutStatus === "active" ||
      billing.companyCheckoutStatus === "cancelled"
    ) {
      variant = "outline";
    }
  } else if (offer) {
    if (counterViewOpen) {
      content = (
        <CounterOfferView
          offer={offer}
          candidate={candidate}
          opportunity={opportunity}
          cancelFn={() => setCounterViewOpen(false)}
        />
      );
    } else if (offer.status === "accepted") {
      content = (
        <AcceptedOfferView
          opportunity={opportunity}
          offer={offer}
          candidate={candidate}
          closeFn={() => setViewOfferDialogOpen(false)}
        />
      );
    } else if (offer.status === "sent" && offer.sender === "company") {
      content = (
        <SentOfferView
          offer={offer}
          candidate={candidate}
          opportunity={opportunity}
          closeFn={() => setViewOfferDialogOpen(false)}
        />
      );
    } else if (offer.status === "sent" && offer.sender === "candidate") {
      content = (
        <ReceivedOfferView
          offer={offer}
          opportunity={opportunity}
          startCounterFn={() => setCounterViewOpen(true)}
          closeFn={() => setViewOfferDialogOpen(false)}
        />
      );
    }
  } else {
    return;
  }

  return (
    <Dialog open={viewOfferDialogOpen} onOpenChange={setViewOfferDialogOpen}>
      <DialogTrigger asChild>
        <Button variant={variant}>View {billing ? "Terms" : "Offer"}</Button>
      </DialogTrigger>
      <DialogContent>{content}</DialogContent>
    </Dialog>
  );
};

const SentOfferView = ({
  offer,
  candidate,
  opportunity,
  closeFn,
}: {
  offer: Offer;
  candidate: CandidateStatus | MinimalCandidateStatus;
  opportunity: CompanyJobOpening | CandidateJobOpening;
  closeFn: () => void;
}) => {
  const candidateName =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    ("name" in candidate && candidate.name?.split(" ")[0]) || "the candidate";

  return (
    <>
      <DialogHeader className="p-6">
        <div className="flex items-center gap-2">
          <DialogTitle className="w-full mr-6">
            <div className="p-4 flex flex-col bg-[#F6F8F8] rounded-lg">
              <div className="flex flex-row items-center self-stretch gap-2">
                <Check size={24} stroke="#1D5249" />
                <p className="text-base text-brand-primary-400 font-semibold">
                  Your offer was sent to {candidateName}
                </p>
              </div>
              {offer.status === "sent" && (
                <p className="pl-8 text-base text-brand-neutral-black-8">
                  We’ll notify you once the candidate responds.
                </p>
              )}
            </div>
          </DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          View the current offer
        </DialogDescription>
      </DialogHeader>
      <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
        <div className="flex flex-col gap-10">
          <div className="flex flex-col">
            <h2 className="text-xl text-[#2E2E38] font-bold">
              Estimated monthly charges
            </h2>
            {offer.status === "sent" && (
              <p className="text-base text-brand-neutral-black-8">
                Based on the offer submitted. If a candidate submits a counter
                offer, you’ll be asked to review prior to being charged.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-4">
            {offer.terms.map((term, idx) => (
              <OfferTermSection key={`term-${idx}`} term={term} />
            ))}
            <div className="!mb-1 flex gap-3 justify-end">
              <PaymentButton
                offer={offer}
                opportunity={opportunity}
                closeDialogFn={closeFn}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </>
  );
};

const ReceivedOfferView = ({
  offer,
  opportunity,
  startCounterFn,
  closeFn,
}: {
  offer: Offer;
  opportunity: CompanyJobOpening | CandidateJobOpening;
  startCounterFn: () => void;
  closeFn: () => void;
}) => {
  const { acceptOffer, isAcceptingOffer } = useAcceptOffer(closeFn);

  return (
    <>
      <DialogHeader className="p-6">
        <DialogTitle className="w-full mr-6 text-3xl text-[#2E2E38] font-bold">
          Review counter offer
        </DialogTitle>
        <DialogDescription className="sr-only">
          View the current offer
        </DialogDescription>
      </DialogHeader>
      <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-4">
            {offer.terms.map((term, idx) => (
              <CounterOfferTermSection key={`term-${idx}`} term={term} />
            ))}
            {offer.note && (
              <div className="flex flex-col gap-1">
                <h3 className="text-base text-[#2E2E38] font-semibold">
                  Note from candidate:
                </h3>
                <p className="text-base text-brand-neutral-black-7">
                  {offer.note}
                </p>
              </div>
            )}
            <div className="!mb-1 flex gap-3 justify-end">
              <Button
                variant="stroke"
                className="mt-2"
                disabled={isAcceptingOffer}
                onClick={startCounterFn}
              >
                Counter offer
              </Button>
              <Button
                variant="success"
                type="submit"
                className="mt-2"
                disabled={isAcceptingOffer}
                onClick={() =>
                  acceptOffer({
                    openingId: opportunity.id,
                    offerId: offer.id,
                  })
                }
              >
                Accept offer
              </Button>
            </div>
          </div>
        </div>
      </ScrollArea>
    </>
  );
};

const CounterOfferView = ({
  offer,
  candidate,
  opportunity,
  cancelFn,
}: {
  offer: Offer;
  candidate: CandidateStatus | MinimalCandidateStatus;
  opportunity: CompanyJobOpening | CandidateJobOpening;
  cancelFn: () => void;
}) => {
  return (
    <>
      <DialogHeader className="p-6">
        <DialogTitle className="w-full mr-6 text-3xl text-[#2E2E38] font-bold">
          Submit counter offer
        </DialogTitle>
        <DialogDescription className="sr-only">
          Submit a counter offer
        </DialogDescription>
      </DialogHeader>
      <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
        <OpportunityCounterOfferForm
          jobOpening={opportunity}
          candidate={candidate}
          offer={offer}
          onCancel={cancelFn}
          onSuccess={cancelFn}
        />
      </ScrollArea>
    </>
  );
};

const AcceptedOfferView = ({
  offer,
  candidate,
  opportunity,
  closeFn,
}: {
  offer: Offer;
  candidate: CandidateStatus | MinimalCandidateStatus;
  opportunity: CompanyJobOpening | CandidateJobOpening;
  closeFn: () => void;
}) => {
  const candidateName =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    ("name" in candidate && candidate.name?.split(" ")[0]) || "the candidate";

  return (
    <>
      <DialogHeader className="p-6">
        <div className="flex items-center gap-2">
          <DialogTitle className="w-full mr-6">
            <div className="p-4 flex flex-col bg-[#F6F8F8] rounded-lg">
              <div className="flex flex-row items-center self-stretch gap-2">
                <Check size={24} stroke="#1D5249" />
                <p className="text-base text-brand-primary-400 font-semibold">
                  {offer.sender === "company"
                    ? `Your offer was accepted by ${candidateName}`
                    : `We’ve let ${candidateName} know you’ve accepted the counter offer`}
                </p>
              </div>
              {offer.status === "sent" && (
                <p className="pl-8 text-base text-brand-neutral-black-8">
                  We’ll send you a confirmation email with the details, and the
                  final contract to sign.
                </p>
              )}
            </div>
          </DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          View the current offer
        </DialogDescription>
      </DialogHeader>
      <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
        <div className="flex flex-col gap-10">
          <div className="flex flex-col">
            <h2 className="text-xl text-[#2E2E38] font-bold">
              Estimated monthly charges
            </h2>
            {offer.status === "sent" && (
              <p className="text-base text-brand-neutral-black-8">
                Based on the offer that was accepted.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-4">
            {offer.terms.map((term, idx) => (
              <OfferTermSection key={`term-${idx}`} term={term} />
            ))}
          </div>
        </div>

        <div className="!mb-1 flex gap-3 justify-between">
          <Button
            variant="stroke"
            type="button"
            className="mt-2"
            onClick={closeFn}
          >
            Close
          </Button>
          <PaymentButton
            opportunity={opportunity}
            offer={offer}
            closeDialogFn={closeFn}
          />
        </div>
      </ScrollArea>
    </>
  );
};

const BillingEstablishedView = ({
  billing,
  candidate,
  opportunity,
  closeFn,
}: {
  billing: Billing;
  candidate: CandidateStatus | MinimalCandidateStatus;
  opportunity: CompanyJobOpening | CandidateJobOpening;
  closeFn: () => void;
}) => {
  const candidateName =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    ("name" in candidate && candidate.name?.split(" ")[0]) || "the candidate";

  const recurring = billing.terms.filter(
    ({ termType }) => termType === "recurring",
  );
  const milestones = billing.terms.filter(
    ({ termType }) => termType === "milestone",
  );

  return (
    <>
      <DialogHeader className="p-6">
        <div className="flex items-center gap-2">
          <DialogTitle className="w-full mr-6">
            <div className="p-4 flex flex-col bg-[#F6F8F8] rounded-lg">
              <div className="flex flex-row items-center self-stretch gap-2">
                <Check size={24} stroke="#1D5249" />
                <p className="text-base text-brand-primary-400 font-semibold">
                  Your engagement with {candidateName} is all set!
                </p>
              </div>
            </div>
          </DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          View the finalized offer details
        </DialogDescription>
      </DialogHeader>
      <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
        {recurring.length ? (
          <div className="flex flex-col gap-10">
            <div className="flex flex-col">
              <h2 className="text-xl text-[#2E2E38] font-bold">
                Monthly charges
              </h2>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <h3 className="text-base text-[#2E2E38] font-semibold">
                    First billing date:
                  </h3>
                  <p className="text-base text-brand-neutral-black-7">
                    {dateStringToLocalDate(
                      billing.firstBillingDate,
                    )?.toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
                {recurring.map((term, idx) => (
                  <BillingTermSection key={`term-${idx}`} term={term} />
                ))}
              </div>
            </div>
          </div>
        ) : null}
        {milestones.length ? (
          <div className="flex flex-col gap-10">
            <div className="flex flex-col">
              <h2 className="text-xl text-[#2E2E38] font-bold">Milestones</h2>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-6">
                {milestones.map((term, idx) => (
                  <BillingTermSection key={`term-${idx}`} term={term} />
                ))}
              </div>
            </div>
          </div>
        ) : null}
        <div className="!mb-1 flex gap-3 justify-end">
          <PaymentButton
            billing={billing}
            opportunity={opportunity}
            closeDialogFn={closeFn}
          />
        </div>
      </ScrollArea>
    </>
  );
};

const BillingTermSection = ({ term }: { term: NewBillingTerm }) => {
  if (term.termType === "milestone") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">Milestone:</h3>
          <p className="text-base text-brand-neutral-black-7">
            {term.milestoneLabel ?? "Unnamed"}
          </p>
        </div>
        <div className="flex gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.candidateAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Venturous fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.platformFeeCents / 100)}{" "}
            (plus tax)
          </p>
        </div>
      </div>
    );
  } else if (term.termType === "recurring") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Monthly retainer:{" "}
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.candidateAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Venturous fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Monthly fee:{" "}
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.platformFeeCents / 100)}{" "}
            (plus tax)
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Billing details:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Duration: {term.iterations ? `${term.iterations} months` : "TBD"}
          </p>
          <p className="text-base text-brand-neutral-black-7">
            Billed on: {term.billingDay} of every month
          </p>
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Salary:{" "}
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.candidateAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Venturous fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.platformFeeCents / 100)}{" "}
            (plus tax)
          </p>
        </div>
      </div>
    );
  }
};

const OfferTermSection = ({ term }: { term: OfferTerm }) => {
  if (term.termType === "milestone") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">Milestone</h3>
          <p className="text-base text-brand-neutral-black-7">
            {term.milestoneLabel ?? "Unnamed"}
          </p>
        </div>
        <div className="flex gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Venturous fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {typeof term.platformFeePercentage === "number"
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  (term.totalAmountCents * (term.platformFeePercentage / 100)) /
                    100,
                )
              : "TBD"}{" "}
            (plus tax)
          </p>
        </div>
      </div>
    );
  } else if (term.termType === "recurring") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Monthly retainer:{" "}
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Venturous fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Monthly fee:{" "}
            {typeof term.platformFeePercentage === "number"
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  (term.totalAmountCents * (term.platformFeePercentage / 100)) /
                    100,
                )
              : "TBD"}{" "}
            (plus tax)
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Billing details:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Duration: {term.iterations ? `${term.iterations} months` : "TBD"}
          </p>
          <p className="text-base text-brand-neutral-black-7">
            Billed on: {term.billingDay} of every month
          </p>
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Salary:{" "}
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Venturous fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {typeof term.platformFeePercentage === "number"
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  (term.totalAmountCents * (term.platformFeePercentage / 100)) /
                    100,
                )
              : "TBD"}{" "}
            (plus tax)
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Start date:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Date(term.startDate).toLocaleDateString()}
          </p>
        </div>
      </div>
    );
  }
};

const CounterOfferTermSection = ({ term }: { term: OfferTerm }) => {
  if (term.termType === "milestone") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">Milestone</h3>
          <p className="text-base text-brand-neutral-black-7">
            {term.milestoneLabel ?? "Unnamed"}
          </p>
        </div>
        <div className="flex gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
      </div>
    );
  } else if (term.termType === "recurring") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Contract duration:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {term.iterations ? `${term.iterations} months` : "TBD"}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Monthly retainer:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Payment cycle:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {term.billingDay} of every month
          </p>
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">Salary:</h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Start date:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Date(term.startDate).toLocaleDateString()}
          </p>
        </div>
      </div>
    );
  }
};

export default ViewOfferCompany;
